"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown, Globe, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
const legacyLogo = "/assets/legacy-logo.png";

const ArrowRightIcon = () => <ArrowRight className="w-3 h-3" />;

interface DropdownItem {
  label: string;
  href: string;
  external?: boolean;
}

interface NavItem {
  label: string;
  href?: string;
  description?: string;
  children?: DropdownItem[];
}

const useNavItems = (): NavItem[] => {
  const { t } = useTranslation();
  return [
    {
      label: t("nav.individuals"),
      href: "/individuals",
      description: t("nav.individualsDescription"),
    },
    {
      label: t("nav.businesses"),
      href: "/businesses",
      description: t("nav.businessesDescription"),
    },
    {
      label: t("nav.howItWorks"),
      href: "/how-it-works",
      description: t("nav.howItWorksDescription"),
    },
    {
      label: t("nav.resources"),
      children: [
        { label: t("nav.faqs"), href: "/faqs" },
        { label: t("nav.blogs"), href: "/blogs" },
        { label: t("nav.news"), href: "/news" },
      ],
    },
    {
      label: t("nav.about"),
      children: [
        { label: t("nav.missionAndVision"), href: "/about" },
        { label: t("nav.meetTheFounder"), href: "/founder" },
        { label: t("nav.contactUs"), href: "/contact" },
      ],
    },
  ];
};

const DesktopDropdown = ({ item }: { item: NavItem }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const ref = useRef<HTMLDivElement>(null);

  const enter = () => {
    clearTimeout(timeout.current);
    setOpen(true);
  };
  const leave = () => {
    timeout.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => () => clearTimeout(timeout.current), []);

  const hasDescription = !!item.description;
  const hasChildren = !!item.children?.length;
  const showPanel = open && (hasDescription || hasChildren);

  return (
    <div ref={ref} className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      {item.href && !hasChildren ? (
        <Link
          href={item.href}
          className="font-button text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground/80 hover:text-accent-blue px-3 py-2 rounded-md hover:bg-accent-blue/5 transition-colors"
        >
          {item.label}
        </Link>
      ) : (
        <button className="font-button text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground/80 hover:text-accent-blue px-3 py-2 rounded-md hover:bg-accent-blue/5 transition-colors flex items-center gap-1">
          {item.label}
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}
      {showPanel && (
        <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-border py-2 min-w-[260px] z-50">
          {hasDescription && (
            item.href ? (
              <Link
                href={item.href}
                onClick={() => setOpen(false)}
                className="block px-4 py-3 hover:bg-secondary transition-colors border-b border-border"
              >
                <p className="font-body text-sm text-foreground/70 leading-relaxed">
                  {item.description}
                </p>
                <span className="mt-2 inline-flex items-center gap-1 font-button text-xs uppercase tracking-wider font-semibold text-accent-blue">
                  {t("nav.visit")} {item.label}
                  <ArrowRightIcon />
                </span>
              </Link>
            ) : (
              <p className="px-4 py-3 font-body text-sm text-foreground/70 leading-relaxed">
                {item.description}
              </p>
            )
          )}
          {hasDescription && hasChildren && <div className="border-t border-border my-1" />}
          {hasChildren &&
            item.children!.map((child) =>
              child.external ? (
                <a
                  key={child.label}
                  href={child.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-2.5 font-body text-sm text-foreground hover:bg-secondary hover:text-accent-blue transition-colors"
                  onClick={() => setOpen(false)}
                >
                  {child.label}
                  <span className="text-xs text-muted-foreground ml-1">↗</span>
                </a>
              ) : (
                <Link
                  key={child.label}
                  href={child.href}
                  className="block px-4 py-2.5 font-body text-sm text-foreground hover:bg-secondary hover:text-accent-blue transition-colors"
                  onClick={() => setOpen(false)}
                >
                  {child.label}
                </Link>
              )
            )}
        </div>
      )}
    </div>
  );
};

const languages = [
  { code: "en", label: "English" },
  { code: "es", label: "Espanol" },
];

const LanguageDropdown = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = languages.find((l) => l.code === i18n.language) || languages[0];
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const ref = useRef<HTMLDivElement>(null);

  const enter = () => { clearTimeout(timeout.current); setOpen(true); };
  const leave = () => { timeout.current = setTimeout(() => setOpen(false), 150); };

  const switchLanguage = (lang: typeof languages[0]) => {
    i18n.changeLanguage(lang.code);
    localStorage.setItem("lang", lang.code);
    setOpen(false);
  };

  useEffect(() => () => clearTimeout(timeout.current), []);

  return (
    <div ref={ref} className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      <button className="flex items-center gap-1.5 font-button text-sm text-foreground hover:text-accent-blue transition-colors h-10 px-4 rounded-full border border-border hover:border-accent-blue">
        <Globe className="w-4 h-4" />
        <span>{current.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-lg border border-border py-1 min-w-[140px] z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => switchLanguage(lang)}
              className={`block w-full text-left px-4 py-2 font-body text-sm transition-colors ${current.code === lang.code ? "text-accent-blue bg-secondary" : "text-foreground hover:bg-secondary hover:text-accent-blue"}`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const { t } = useTranslation();
  const navItems = useNavItems();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null);
  const router = useRouter();

  const toggleMobileDropdown = (label: string) => {
    setExpandedMobile(expandedMobile === label ? null : label);
  };

  return (
    <nav className="sticky top-0 left-0 right-0 z-50 bg-white shadow-sm">
      <div className="max-w-[1440px] mx-auto px-6 min-[1060px]:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center">
            <img
              src={legacyLogo}
              alt="Legacy Healthcare Solutions"
              className="h-[72px] w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden min-[1060px]:flex items-center gap-1 pl-4 ml-2 border-l border-border" aria-label="Primary">
            {navItems.map((item) =>
              item.children || item.description ? (
                <DesktopDropdown key={item.label} item={item} />
              ) : (
                <Link
                  key={item.label}
                  href={item.href!}
                  className="font-button text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground/80 hover:text-accent-blue px-3 py-2 rounded-md hover:bg-accent-blue/5 transition-colors"
                >
                  {item.label}
                </Link>
              )
            )}
          </div>

          {/* Desktop CTA + Language */}
          <div className="hidden min-[1060px]:flex items-center gap-3">
            <LanguageDropdown />
            <a
              href="https://legacyhealthcare.navigohealth.com/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 h-10 px-4 rounded-full border border-border text-foreground hover:text-accent-blue hover:border-accent-blue transition-colors font-button text-sm whitespace-nowrap"
              aria-label="Login"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M23.5641 20.5481C21.0499 18.0342 17.5903 16.5855 13.9837 16.1932C16.492 15.3473 18.3042 12.9738 18.3042 10.1834C18.3042 6.6867 15.4596 3.84186 11.9632 3.84186C8.46679 3.84186 5.62219 6.6867 5.62219 10.1834C5.62219 12.9757 7.43671 15.3506 9.9478 16.1951C6.34711 16.5897 2.89382 18.038 0.38366 20.5481C0.243044 20.6887 0.243044 20.9165 0.38366 21.0572C0.45398 21.1275 0.546092 21.1626 0.638204 21.1626C0.730321 21.1626 0.822428 21.1275 0.892724 21.0572C6.5885 15.3614 17.3587 15.3614 23.055 21.0572C23.1956 21.1978 23.4234 21.1978 23.5641 21.0572C23.7047 20.9165 23.7047 20.6887 23.5641 20.5481ZM6.34219 10.1834C6.34219 7.08373 8.86382 4.56186 11.9632 4.56186C15.0626 4.56186 17.5842 7.08373 17.5842 10.1834C17.5842 13.2832 15.0626 15.8053 11.9632 15.8053C8.86382 15.8053 6.34219 13.2832 6.34219 10.1834Z" fill="currentColor"/>
              </svg>
              {t("nav.login")}
            </a>
            <Button
              onClick={() => window.open("https://support.legacy-hc.com/widget/form/TgnMv3pkwXzQzfAvbwXs", "_blank")}
              className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-5 py-4 font-button text-sm cta-hover whitespace-nowrap"
            >
              {t("nav.joinLegacyToday")}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="min-[1060px]:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-foreground" />
            ) : (
              <Menu className="w-6 h-6 text-foreground" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="min-[1060px]:hidden pb-6">
            <div className="flex flex-col">
              {navItems.map((item) => {
                const hasChildren = !!item.children?.length;
                const hasDescription = !!item.description;
                if (hasChildren || hasDescription) {
                  return (
                    <div key={item.label} className="border-b border-border last:border-b-0">
                      <button
                        onClick={() => toggleMobileDropdown(item.label)}
                        className="flex items-center justify-between w-full font-button text-sm text-foreground hover:text-accent-blue transition-colors py-3"
                      >
                        {item.label}
                        <ChevronDown className={`w-4 h-4 transition-transform ${expandedMobile === item.label ? "rotate-180" : ""}`} />
                      </button>
                      {expandedMobile === item.label && (
                        <div className="pl-4 pb-3 flex flex-col gap-1">
                          {hasDescription && (
                            item.href && !hasChildren ? (
                              <Link
                                href={item.href}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="font-body text-sm text-foreground/70 leading-relaxed py-2 hover:text-accent-blue transition-colors"
                              >
                                {item.description}
                              </Link>
                            ) : (
                              <p className="font-body text-sm text-foreground/70 leading-relaxed py-2">
                                {item.description}
                              </p>
                            )
                          )}
                          {hasDescription && hasChildren && <div className="border-t border-border my-1" />}
                          {hasChildren &&
                            item.children!.map((child) =>
                              child.external ? (
                                <a
                                  key={child.label}
                                  href={child.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-body text-sm text-foreground/80 hover:text-accent-blue py-2 transition-colors"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  {child.label} ↗
                                </a>
                              ) : (
                                <Link
                                  key={child.label}
                                  href={child.href}
                                  className="font-body text-sm text-foreground/80 hover:text-accent-blue py-2 transition-colors"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  {child.label}
                                </Link>
                              )
                            )}
                          {!hasChildren && item.href && (
                            <Link
                              href={item.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className="inline-flex items-center gap-1 font-button text-xs uppercase tracking-wider font-semibold text-accent-blue py-2 transition-colors"
                            >
                              {t("nav.visit")} {item.label}
                              <ArrowRight className="w-3 h-3" />
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Link
                    key={item.label}
                    href={item.href!}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-3 border-b border-border last:border-b-0 font-button text-sm text-foreground hover:text-accent-blue transition-colors"
                  >
                    {item.label}
                  </Link>
                );
              })}
              <div className="mt-4">
                <Button
                  onClick={() => {
                    window.open("https://support.legacy-hc.com/widget/form/TgnMv3pkwXzQzfAvbwXs", "_blank");
                    setIsMobileMenuOpen(false);
                  }}
                  className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-5 py-4 font-button text-sm w-full cta-hover"
                >
                  {t("nav.joinLegacyToday")}
                </Button>
              </div>
              <div className="mt-3">
                <LanguageDropdown />
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
